/**
 * AccountDetail.tsx — Single-account drill-in.
 * Wired to live Supabase data.
 *
 * Route: /reporting/account-detail?id=<accountId>
 * Optional: ?question=cpN  → scrolls to that CP question block
 *
 * Sections (top→bottom):
 *  1. Header     — name, AE owner, value, bucket, last updated
 *  2. CP grid    — 10 checkpoints w/ status pills (account_cp_status)
 *  3. Next Steps — W15: top 3 unanswered CPs from question_bank + account_cp_status (view-only)
 *  4. Questions  — account_answers grouped by CP (question_key prefix "cpN_…")
 *  5. Decision makers — decision_makers list
 *  6. Activity   — hunt_items DESC last 20
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import NextStepsPanel from "@/components/account/NextStepsPanel";
import TemplateLibraryBrowser from "@/components/templates/TemplateLibraryBrowser";
import ComposeModal, { type ComposeChannel } from "@/components/cadence/ComposeModal";
import { type TemplateScript, type ScriptChannel } from "@/lib/template-library";
import {
  AccountRow,
  AnswerRow,
  CP_LABELS,
  CpStatusRow,
  DecisionMakerRow,
  ForecastRow,
  HuntItemRow,
  UserRow,
  bucketColor,
  daysSince,
  fmtUSD,
  getAccount,
  listAccountForecasts,
  listAllUsers,
  listAnswersFor,
  listCpStatusFor,
  listDecisionMakersFor,
  listHuntFor,
} from "@/lib/reporting-live";

function readHashParams(): URLSearchParams {
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const q = hash.indexOf("?");
  return new URLSearchParams(q >= 0 ? hash.slice(q + 1) : "");
}

// Gate status colors — canon per CANON_VISUALS_LOCKED.md (locked 2026-05-27).
// 5 states: gray (unscored) / red / orange / yellow / green.
function statusColor(s: string | null): string {
  switch (s) {
    case "green":
      return "#16A34A"; // canon green
    case "yellow":
    case "amber": // legacy alias, treated as yellow
      return "#EAB308"; // canon yellow
    case "orange":
      return "#EA580C"; // canon orange
    case "red":
      return "#DC2626"; // canon red
    default:
      return "#6B7280"; // canon gray (unscored)
  }
}

function statusLabel(s: string | null): string {
  if (!s) return "Unscored";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Extract a CP number from a question_key. Convention: "cp3_pain_dollarized" or
// "cp_3_…" or just any digits after "cp". Returns 0 if unparseable.
function questionCp(key: string): number {
  const m = /^cp[_-]?(\d{1,2})/i.exec(key);
  return m ? parseInt(m[1], 10) : 0;
}

interface State {
  loading: boolean;
  error: string | null;
  account: AccountRow | null;
  owner: UserRow | null;
  forecast: ForecastRow | null;
  cpStatus: CpStatusRow[];
  answers: AnswerRow[];
  decisionMakers: DecisionMakerRow[];
  hunt: HuntItemRow[];
}

const EMPTY: State = {
  loading: true,
  error: null,
  account: null,
  owner: null,
  forecast: null,
  cpStatus: [],
  answers: [],
  decisionMakers: [],
  hunt: [],
};

export default function AccountDetail() {
  const [state, setState] = useState<State>(EMPTY);
  const params = readHashParams();
  const accountId = parseInt(params.get("id") || "0", 10);
  const focusCp = params.get("question") || "";

  // Template browser → compose modal flow
  const [tplOpen, setTplOpen] = useState(false);
  const [pickedScript, setPickedScript] = useState<TemplateScript | null>(null);

  function scriptChannelToCompose(c: ScriptChannel): ComposeChannel {
    if (c === "phone_script" || c === "voicemail_script") return "phone_with_vm";
    return c;
  }

  useEffect(() => {
    if (!accountId) {
      setState({ ...EMPTY, loading: false, error: "No account id provided." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const account = await getAccount(accountId);
        if (cancelled) return;
        if (!account) {
          setState({ ...EMPTY, loading: false, error: "Account not found in this tenant." });
          return;
        }
        const [users, forecasts, cpStatus, answers, decisionMakers, hunt] = await Promise.all([
          listAllUsers(),
          listAccountForecasts(),
          listCpStatusFor([accountId]),
          listAnswersFor(accountId),
          listDecisionMakersFor(accountId),
          listHuntFor(accountId, 20),
        ]);
        if (cancelled) return;
        const owner = users.find((u) => u.id === account.owner_user_id) || null;
        const forecast = forecasts.find((f) => f.account_id === accountId) || null;
        setState({
          loading: false,
          error: null,
          account,
          owner,
          forecast,
          cpStatus,
          answers,
          decisionMakers,
          hunt,
        });
      } catch (err: any) {
        if (cancelled) return;
        setState({ ...EMPTY, loading: false, error: err?.message || "Failed to load" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // Once loaded, scroll to ?question=cpN if requested.
  useEffect(() => {
    if (!state.loading && focusCp) {
      const el = document.getElementById(`question-${focusCp.toLowerCase()}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [state.loading, focusCp]);

  const cpByNumber = useMemo(() => {
    const m = new Map<number, CpStatusRow>();
    state.cpStatus.forEach((c) => m.set(c.cp_number, c));
    return m;
  }, [state.cpStatus]);

  const answersByCp = useMemo(() => {
    const m = new Map<number, AnswerRow[]>();
    for (const a of state.answers) {
      const cp = questionCp(a.question_key);
      if (!m.has(cp)) m.set(cp, []);
      m.get(cp)!.push(a);
    }
    return m;
  }, [state.answers]);

  if (state.loading) {
    return (
      <Shell>
        <div style={{ padding: 40, textAlign: "center", color: "#8E8E94" }}>Loading…</div>
      </Shell>
    );
  }

  if (state.error || !state.account) {
    return (
      <Shell>
        <div
          style={{
            padding: 16,
            background: "#FEF2F2",
            border: "1px solid #FCA5A5",
            borderRadius: 8,
            color: "#991B1B",
            fontSize: 13,
            margin: "20px 0",
          }}
        >
          {state.error || "Account not found."}
        </div>
      </Shell>
    );
  }

  const a = state.account;
  const bucket = (state.forecast?.forecast_bucket || a.bucket || "").toUpperCase();
  const value = state.forecast?.deal_value ?? a.est_arr ?? 0;
  const lastUpdatedTs =
    [a.updated_at, ...state.cpStatus.map((c) => c.updated_at), ...state.answers.map((x) => x.updated_at)]
      .filter(Boolean)
      .sort()
      .pop() || a.updated_at;

  // Build a synthetic compose prospect from the account + first decision maker.
  const composeProspect = useMemo(() => {
    const dm = state.decisionMakers[0];
    if (dm) {
      const parts = dm.name.trim().split(" ");
      return {
        full_name: dm.name,
        first_name: parts[0] || "",
        title: dm.title || "",
        company: state.account?.name || "",
      };
    }
    return {
      full_name: state.account?.name || "Decision maker",
      first_name: "",
      title: "",
      company: state.account?.name || "",
    };
  }, [state.decisionMakers, state.account]);

  return (
    <Shell>
      {/* Template browser */}
      <TemplateLibraryBrowser
        open={tplOpen}
        onClose={() => setTplOpen(false)}
        onPick={(s) => setPickedScript(s)}
        title="Compose with template"
      />
      {pickedScript && (
        <ComposeModal
          open={true}
          onClose={() => setPickedScript(null)}
          scriptId={pickedScript.id}
          channel={scriptChannelToCompose(pickedScript.channel)}
          prospect={composeProspect}
          onSent={() => setPickedScript(null)}
        />
      )}

      {/* Header */}
      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E5E5E7",
          borderRadius: 12,
          padding: 24,
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: "#0B0B0F", margin: 0 }}>{a.name}</h1>
            <div style={{ fontSize: 13, color: "#6B6B70", marginTop: 4 }}>
              {a.industry || "—"} · Stage: {a.stage || "—"}
            </div>
          </div>
          <Field label="Owner AE" value={state.owner?.name || state.owner?.email || `user ${a.owner_user_id}`} />
          <Field label="Deal value" value={fmtUSD(value)} />
          <Field
            label="Bucket"
            value={
              bucket ? (
                <span
                  style={{
                    padding: "2px 10px",
                    borderRadius: 4,
                    background: bucketColor(bucket) + "22",
                    color: bucketColor(bucket),
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                  }}
                >
                  {bucket}
                </span>
              ) : (
                "—"
              )
            }
          />
          <Field label="Last updated" value={`${daysSince(lastUpdatedTs)}d ago`} />
        </div>
        {/* Quick actions */}
        <div
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTop: "1px solid #E5E5E7",
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => setTplOpen(true)}
            style={{
              background: "#0B0B0F",
              color: "#FFFFFF",
              border: "none",
              padding: "8px 14px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Compose with template
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const { api } = await import("@/lib/session");
                const { supabase } = await import("@/lib/session");
                const { data } = await supabase().auth.getSession();
                const token = data.session?.access_token;
                const resp = await fetch(`/api/accounts/${a.id}/export.pdf`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!resp.ok) {
                  const msg = `Export failed (${resp.status})`;
                  alert(msg);
                  return;
                }
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `account-${a.id}-${new Date().toISOString().slice(0, 10)}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                // Suppress unused import lint
                void api;
              } catch (e: any) {
                alert(`Export failed: ${e?.message || "unknown error"}`);
              }
            }}
            style={{
              background: "#FFFFFF",
              color: "#DC2626",
              border: "1px solid #DC2626",
              padding: "8px 14px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* CP grid */}
      <Section title="Checkpoints" subtitle="10 checkpoints · status from account_cp_status">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: 8,
          }}
        >
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
            const cp = cpByNumber.get(n);
            return (
              <a
                key={n}
                href={`#/reporting/account-detail?id=${a.id}&question=cp${n}`}
                style={{
                  textDecoration: "none",
                  background: "#FFFFFF",
                  border: "1px solid #E5E5E7",
                  borderRadius: 8,
                  padding: 12,
                  display: "block",
                  borderLeft: `4px solid ${statusColor(cp?.status || null)}`,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#8E8E94",
                  }}
                >
                  CP{n}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0B0B0F", marginTop: 2 }}>
                  {CP_LABELS[n]}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: statusColor(cp?.status || null),
                    fontWeight: 700,
                    marginTop: 4,
                  }}
                >
                  {statusLabel(cp?.status || null)}
                  {cp?.is_manual ? " · manual" : ""}
                </div>
              </a>
            );
          })}
        </div>
      </Section>

      {/* Next Steps — W15: unanswered CP questions in priority order */}
      <section style={{ marginTop: 24 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0B0B0F" }}>Next Steps</div>
          <div style={{ fontSize: 12, color: "#8E8E94" }}>Top 3 open checkpoints for this account</div>
        </div>
        <NextStepsPanel accountId={a.id} />
      </section>

      {/* Questions grouped by CP */}
      <Section title="Discovery" subtitle={`${state.answers.length} answer${state.answers.length === 1 ? "" : "s"} on file`}>
        {state.answers.length === 0 ? (
          <Empty text="No discovery answers recorded yet." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
              const items = answersByCp.get(n) || [];
              if (items.length === 0) return null;
              return (
                <div
                  key={n}
                  id={`question-cp${n}`}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E5E5E7",
                    borderRadius: 8,
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#0B0B0F",
                      marginBottom: 10,
                    }}
                  >
                    CP{n} · {CP_LABELS[n]}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {items.map((q) => (
                      <div
                        key={q.question_key}
                        style={{
                          paddingLeft: 12,
                          borderLeft: q.is_assumption
                            ? "3px solid #CA8A04"
                            : "3px solid #E5E5E7",
                        }}
                      >
                        <div style={{ fontSize: 12, color: "#525258", fontWeight: 600 }}>
                          {q.question_key}
                          {q.is_assumption && (
                            <span
                              style={{
                                marginLeft: 8,
                                padding: "1px 6px",
                                background: "#FEF3C7",
                                color: "#92400E",
                                borderRadius: 3,
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                              }}
                            >
                              ASSUMPTION
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 13, color: "#0B0B0F", marginTop: 2 }}>
                          {q.selected_option || "—"}
                        </div>
                        {q.evidence && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#6B6B70",
                              fontStyle: "italic",
                              marginTop: 4,
                            }}
                          >
                            "{q.evidence}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {/* Catch-all for answers whose CP couldn't be parsed */}
            {(answersByCp.get(0) || []).length > 0 && (
              <div
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E5E5E7",
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#8E8E94",
                    marginBottom: 10,
                  }}
                >
                  Other answers
                </div>
                {(answersByCp.get(0) || []).map((q) => (
                  <div key={q.question_key} style={{ fontSize: 12, color: "#525258" }}>
                    {q.question_key}: {q.selected_option || "—"}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Decision makers */}
      <Section title="Decision makers" subtitle={`${state.decisionMakers.length} mapped`}>
        {state.decisionMakers.length === 0 ? (
          <Empty text="No decision makers mapped yet." />
        ) : (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FAFAF7" }}>
                  {["Name", "Title", "Strength"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 14px",
                        textAlign: "left",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#8E8E94",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.decisionMakers.map((d) => {
                  const s = d.relationship_strength ?? d.strength ?? 0;
                  return (
                    <tr key={d.id} style={{ borderTop: "1px solid #EFEFEE" }}>
                      <td style={{ padding: "8px 14px", color: "#0B0B0F", fontWeight: 600 }}>
                        {d.name}
                      </td>
                      <td style={{ padding: "8px 14px", color: "#525258" }}>{d.title || "—"}</td>
                      <td style={{ padding: "8px 14px", color: "#0B0B0F" }}>
                        {"●".repeat(s)}
                        <span style={{ color: "#C9C9CC" }}>{"●".repeat(Math.max(0, 5 - s))}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Activity */}
      <Section title="Activity" subtitle={`Last ${state.hunt.length} hunt items`}>
        {state.hunt.length === 0 ? (
          <Empty text="No hunt activity recorded." />
        ) : (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 8,
              padding: 8,
            }}
          >
            {state.hunt.map((h) => (
              <div
                key={h.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "8px 12px",
                  borderBottom: "1px solid #FAFAF7",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: "#0B0B0F", fontWeight: 600 }}>
                    {h.label || h.item_key || "(unlabeled)"}
                  </div>
                  {h.evidence && (
                    <div style={{ fontSize: 12, color: "#6B6B70", fontStyle: "italic" }}>
                      "{h.evidence}"
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "#8E8E94", marginTop: 2 }}>
                    Status: {h.status} · Owner: {h.owner || "—"}
                    {h.linked_cp ? ` · CP${h.linked_cp}` : ""}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#8E8E94", whiteSpace: "nowrap" }}>
                  {daysSince(h.updated_at)}d ago
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </Shell>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <OperatorShell breadcrumbs={[{ label: "Reporting", href: "/reporting" }, { label: "Accounts in Flight", href: "/reporting/accounts" }, { label: "Account Detail" }]}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {children}
      </div>
    </OperatorShell>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#8E8E94",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#0B0B0F" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "#8E8E94" }}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px dashed #C9C9CC",
        borderRadius: 8,
        padding: 16,
        color: "#8E8E94",
        fontSize: 13,
        textAlign: "center",
      }}
    >
      {text}
    </div>
  );
}
